import { useCallback, useRef, useState } from "react"

import { DEFAULT_SEARCH_FILTERS_DRAFT, type SearchFiltersDraft } from "@/lib/search-filter-model"

export type SearchReviewSource = "manual" | "ai" | "user" | "mixed"

export type SearchFiltersUpdater =
  | SearchFiltersDraft
  | ((current: SearchFiltersDraft) => SearchFiltersDraft)

export const cloneSearchFiltersDraft = (filters: SearchFiltersDraft): SearchFiltersDraft => ({
  ...filters,
  locations: [...filters.locations],
  locationRegion: [...filters.locationRegion],
  locationFull: filters.locationFull.map((location) => ({ ...location })),
  jobTitles: [...filters.jobTitles],
  timeSpentMin: [...filters.timeSpentMin],
  jobTitleLevels: [...filters.jobTitleLevels],
  companies: [...filters.companies],
  excludedCompanies: [...filters.excludedCompanies],
  companyIndustries: [...filters.companyIndustries],
  companySizes: [...filters.companySizes],
  skills: [...filters.skills],
  keywords: [...filters.keywords],
})

const resolveNextFilters = (
  current: SearchFiltersDraft,
  nextValue: SearchFiltersUpdater,
): SearchFiltersDraft => {
  if (typeof nextValue === "function") {
    return cloneSearchFiltersDraft(nextValue(cloneSearchFiltersDraft(current)))
  }

  return cloneSearchFiltersDraft(nextValue)
}

interface UseSearchReviewStateOptions {
  initialFilters?: SearchFiltersDraft
  initialSource?: SearchReviewSource
}

/**
 * Owns the editable search filter draft and its provenance.
 * Async parsing/counting layers compose on top of this state instead of mixing
 * network orchestration and draft mutations in one hook.
 */
export function useSearchReviewState({
  initialFilters = DEFAULT_SEARCH_FILTERS_DRAFT,
  initialSource = "manual",
}: UseSearchReviewStateOptions = {}) {
  const [filters, setFilters] = useState<SearchFiltersDraft>(() => cloneSearchFiltersDraft(initialFilters))
  const [reviewSource, setReviewSource] = useState<SearchReviewSource>(initialSource)
  const filtersRef = useRef<SearchFiltersDraft>(cloneSearchFiltersDraft(initialFilters))

  const replaceFilters = useCallback(
    (nextFilters: SearchFiltersDraft, source: SearchReviewSource = "manual") => {
      const clonedFilters = cloneSearchFiltersDraft(nextFilters)
      filtersRef.current = clonedFilters
      setFilters(clonedFilters)
      setReviewSource(source)
      return clonedFilters
    },
    [],
  )

  const resetFilters = useCallback(
    (source: SearchReviewSource = "manual") => replaceFilters(DEFAULT_SEARCH_FILTERS_DRAFT, source),
    [replaceFilters],
  )

  const setFiltersFromUser = useCallback(
    (nextValue: SearchFiltersUpdater) => {
      const nextFilters = resolveNextFilters(filtersRef.current, nextValue)
      const nextSource = reviewSource === "ai" || reviewSource === "mixed" ? "mixed" : "user"

      filtersRef.current = nextFilters
      setFilters(nextFilters)
      setReviewSource(nextSource)

      return nextFilters
    },
    [reviewSource],
  )

  return {
    filters,
    filtersRef,
    replaceFilters,
    resetFilters,
    reviewSource,
    setFiltersFromUser,
  }
}
